import type { SketchMathPreviewMesh } from '../../services/sketchmath';
export type ProjectedPoint = {x:number;y:number;z:number};
const colors:Record<string,number[]>={top:[143,211,255],bottom:[53,82,104],outer_wall:[91,159,210],hole_wall:[34,67,87]};
const cross=(a:number[],b:number[])=>[a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];
const normal=(a:number[],b:number[],c:number[])=>{const n=cross(b.map((v,i)=>v-a[i]),c.map((v,i)=>v-a[i]));const length=Math.hypot(...n)||1;return n.map(v=>v/length);};

// Only open boundaries, surface transitions and sharp creases are feature edges.
// Coplanar triangle diagonals and faceted cylinder seams are not model features.
export function featureEdges(mesh:SketchMathPreviewMesh): [number,number][] {
  const edges=new Map<string,{pair:[number,number];normals:number[][];surfaces:string[]}>();
  for(const t of mesh.triangles){const [a,b,c]=t.indices;const n=normal(mesh.vertices[a],mesh.vertices[b],mesh.vertices[c]);
    for(const pair of [[a,b],[b,c],[c,a]]){pair.sort((x,y)=>x-y);const key=pair.join(':');const e=edges.get(key)||{pair:pair as [number,number],normals:[],surfaces:[]};e.normals.push(n);e.surfaces.push(t.surface);edges.set(key,e);}
  }
  return Array.from(edges.values()).filter(e=>e.normals.length!==2 || e.surfaces[0]!==e.surfaces[1] || Math.abs(e.normals[0].reduce((s,v,i)=>s+v*e.normals[1][i],0))<Math.cos(Math.PI/6)).map(e=>e.pair);
}

// Depth is interpolated at each pixel, not approximated by triangle centroid.
// This preserves through openings and occludes walls behind the front face.
export function rasterizeSolid(mesh:SketchMathPreviewMesh, points:ProjectedPoint[], width:number,height:number, edges=featureEdges(mesh)) {
  const pixels=new Uint8ClampedArray(width*height*4), depth=new Float32Array(width*height);depth.fill(-Infinity);
  for(let i=0;i<pixels.length;i+=4){pixels[i]=7;pixels[i+1]=16;pixels[i+2]=24;pixels[i+3]=255;}
  for(const t of mesh.triangles){const [a,b,c]=t.indices.map(i=>points[i]);
    const denominator=(b.y-c.y)*(a.x-c.x)+(c.x-b.x)*(a.y-c.y);if(Math.abs(denominator)<1e-10)continue;
    const shade=colors[t.surface]||colors.outer_wall;
    const xmin=Math.max(0,Math.floor(Math.min(a.x,b.x,c.x))),xmax=Math.min(width-1,Math.ceil(Math.max(a.x,b.x,c.x)));
    const ymin=Math.max(0,Math.floor(Math.min(a.y,b.y,c.y))),ymax=Math.min(height-1,Math.ceil(Math.max(a.y,b.y,c.y)));
    for(let y=ymin;y<=ymax;y++)for(let x=xmin;x<=xmax;x++){
      const u=((b.y-c.y)*(x+.5-c.x)+(c.x-b.x)*(y+.5-c.y))/denominator;
      const v=((c.y-a.y)*(x+.5-c.x)+(a.x-c.x)*(y+.5-c.y))/denominator;const w=1-u-v;
      if(u< -1e-8||v< -1e-8||w< -1e-8)continue;
      const z=u*a.z+v*b.z+w*c.z,index=y*width+x;if(z<depth[index])continue;
      depth[index]=z;const p=index*4;pixels[p]=shade[0];pixels[p+1]=shade[1];pixels[p+2]=shade[2];
    }
  }
  for(const [ia,ib] of edges){const a=points[ia],b=points[ib];const steps=Math.max(1,Math.ceil(Math.hypot(b.x-a.x,b.y-a.y)));
    for(let j=0;j<=steps;j++){const t=j/steps,x=Math.floor(a.x+(b.x-a.x)*t),y=Math.floor(a.y+(b.y-a.y)*t);if(x<0||x>=width||y<0||y>=height)continue;
      const index=y*width+x,z=a.z+(b.z-a.z)*t;
      // Bias only by the depth span of one pixel on this edge.
      if(z+Math.abs(b.z-a.z)/steps+1e-5<depth[index])continue;
      const p=index*4;pixels[p]=56;pixels[p+1]=111;pixels[p+2]=145;
    }
  }
  return pixels;
}
